import { useQueryClient } from '@tanstack/react-query'
import { Button, Col, DatePicker, Flex, Form, Input, Modal, Row } from 'antd'
import { ModalProps } from 'antd/lib'
import dayjs from 'dayjs'
import { useParams } from 'react-router-dom'

import {
  useInstitutionServiceCreateTechnicalInspectionEndpoint,
  useInstitutionServiceGetListTechnicalInspectionEndpointKey
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { CustomItemRender, message, Upload, SaveConfirm } from 'src/components'
import { useFileHandler } from 'src/hooks'
import { dayMonthYearFormatWithDot, disabledFutureDate } from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

type Props = {
  onCancel: () => void
} & ModalProps
export const TechnicalInspectionsModal = ({ onCancel, ...props }: Props) => {
  const { t } = useLocales()
  const { id } = useParams()

  const [form] = Form.useForm()
  const queryClient = useQueryClient()

  const { onFileChange, saveFiles } = useFileHandler({
    form,
    folder: `technical-inspections`
  })

  const { mutate, isPending } =
    useInstitutionServiceCreateTechnicalInspectionEndpoint({
      onError: (e: ApiError) => formErrorHandler(e.body.errors),
      onSuccess: () => {
        message.success(t('equipments.success_create_inspection'))
        queryClient.invalidateQueries({
          queryKey: [useInstitutionServiceGetListTechnicalInspectionEndpointKey]
        })
        onCancel()
      }
    })

  const onSave = async () => {
    const values = await form.validateFields()

    const updatedFiles = await saveFiles()

    mutate({
      deviceId: String(id),
      requestBody: {
        ...values,
        executionDate: dayjs(values.executionData).format(),
        mediaFiles: updatedFiles
      }
    })
  }

  return (
    <Modal
      onCancel={onCancel}
      open
      centered
      maskClosable={false}
      footer={null}
      title={t('equipments.add_inspection')}
      {...props}
    >
      <Form layout="vertical" requiredMark={false} form={form}>
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <Form.Item
              name="executionDate"
              label={t('equipments.examination_date')}
              rules={requiredRules}
            >
              <DatePicker
                disabledDate={disabledFutureDate}
                format={dayMonthYearFormatWithDot}
                className="full-width"
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              name="docs"
              className="full-width"
              label={t('stock_applications.uploaded_documents')}
            >
              <Upload
                onChange={onFileChange}
                customRequest={() => null}
                itemRender={(_, file, __, { remove }) => (
                  <CustomItemRender file={file} onRemove={remove} />
                )}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item name="description" label={t('common.description')}>
              <Input.TextArea placeholder={t('common.description')} />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Flex justify="center" gap={16}>
              <Button onClick={onCancel}>{t('common.cancel')}</Button>

              <SaveConfirm
                title={`${t('common.save')}?`}
                onConfirm={onSave}
                okButtonProps={{ loading: isPending }}
              >
                <Button type="primary">{t('common.save')}</Button>
              </SaveConfirm>
            </Flex>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}
